import { BaseAxiosClient } from "../../axios/baseAxios.client";
import {
  FeatureEnv,
  FeatureName,
  FeatureServiceOptions,
  GetFeaturesResponse,
  GetFeaturesResult,
} from "./features-api.types";

export class FeaturesApiClient extends BaseAxiosClient {
  baseUrl: string;
  targetEnv: FeatureEnv;

  constructor({ targetEnv, baseUrl, rateLimitConfig }: FeatureServiceOptions) {
    super(rateLimitConfig ? { rateLimitConfig } : {});

    if (!!!baseUrl) {
      throw new Error("Missing features-api baseURL");
    }

    this.baseUrl = baseUrl;
    this.targetEnv = this.resolveFeaturesEnv(targetEnv);
  }

  async getFeatures({
    featureNames,
    ignoreCache,
  }: {
    featureNames: FeatureName[];
    ignoreCache?: boolean;
  }): Promise<GetFeaturesResult> {
    const response = await this.instance.get<GetFeaturesResponse>(
      `${this.baseUrl}/features/v1/environments/${
        this.targetEnv
      }/features/${featureNames.join(",")}`,
      { params: this._buildCacheOption(ignoreCache) }
    );

    if (response.status !== 200) {
      throw new Error(
        `[Features Api Client] Request failed with status ${response.status}`
      );
    }
    return this._parseFeatureResponse(response.data);
  }

  private _parseFeatureResponse(featureResponse: GetFeaturesResponse) {
    const values: GetFeaturesResult["values"] = {};
    Object.entries(featureResponse.values).forEach(([feature, rawValue]) => {
      const value = `${rawValue}`.toLowerCase();

      const newValue: { enabled: boolean; clinicIds?: number[] } = {
        enabled: false,
      };

      if (value.match(/^\d{1,}(\,\d{1,})*$/)) {
        // List of clinic ids
        newValue.clinicIds = value.split(",").map(Number);
        newValue.enabled = newValue.clinicIds.length > 0;
      } else if (["true", "false"].includes(value)) {
        // Booleans
        newValue.enabled = value === "true";
      }

      values[feature as FeatureName] = newValue;
    });
    return { ...featureResponse, values };
  }

  private _buildCacheOption(ignoreCache: boolean | undefined) {
    return { ...(!!ignoreCache ? { cache: "ignore" } : {}) };
  }

  private resolveFeaturesEnv(env: string) {
    switch (env) {
      case "production":
      case "prod":
        return FeatureEnv.prod;
      case "beta":
        return FeatureEnv.beta;
      case "staging":
      case "stage":
        return FeatureEnv.stage;
      case "development":
      case "dev":
        return FeatureEnv.dev;
      case "test":
        return FeatureEnv.test;
      default:
        return FeatureEnv.stage;
    }
  }
}
